import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
// import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAppDispatch } from "../redux/hooks";
import { openAuth } from "../redux/slice/authModalSlice";
import { useScrollToTop } from "../hooks/useScrollToTop";
import PublicRoute from "./PublicRoute";
import TournamentsDetails from "../views/leaguesHome/tournaments/tournamentsDetails/TournamentsDetails";
import CreateTournament from "../views/leaguesHome/tournaments/CreateTournament";
import TeamInfo from "../views/leaguesHome/teams/teamInfo/TeamInfo";
import CreateDivisionPage from "../views/leaguesHome/teams/CreateDivisionPage";
import AddPlayersPage from "../views/leaguesHome/teams/AddPlayersPage";
import PlayerProfile from "../views/leaguesHome/teams/teamInfo/PlayerProfile";
import LoginPage from "../views/auth/LoginPage";
import MatchesDetails from "../views/leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/MatchesDetails";
import Matches from "../views/leaguesHome/tournaments/tournamentsDetails/matches/Matches";
import ScheduleMatchPage from "../views/leaguesHome/tournaments/tournamentsDetails/matches/ScheduleMatchPage";
import TabContentWrapper from "./TabContentWrapper";
import GroundDetails from "../views/leaguesHome/ground/GroundDetails";
import StatGpt from "../views/statgpt/StatGpt";
import PricingPlans from "../views/subscription/PricingPlans";
import HomePage from "../views/homePage/HomePage";
import LoggedInHomePage from "../views/loggedInHome/LoggedInHomePage";
import AboutPage from "../views/about/AboutPage";
import AiHighlightsPage from "../views/aiHighlights/ai_highlights";
import ScoringPage from "../views/scoring/ScoringPage";
import StreamerDashboard from "../views/streamer/StreamerDashboard";
import AssetSetup from "../views/streamer/AssetSetup";
import ParentHeader from "../components/header/ParentHeader";
import Footer from "../components/footer/Footer";
import { getAuthToken, hasPremiumAccess } from "../helper/helper";
import MyTeam from "../views/myTeam/MyTeam";
import MyTeamDetail from "../views/myTeam/MyTeamDetail";
import MyLeagues from "../views/myLeagues/MyLeagues";
import LeagueRouteHydrator from "./LeagueRouteHydrator";
import TournamentRouteHydrator from "./TournamentRouteHydrator";
import CreateLeague from "../views/myLeagues/CreateLeague";
import LeagueManagerComingSoon from "../views/common/LeagueManagerComingSoon";
import LiveStreamingComingSoon from "../views/common/LiveStreamingComingSoon";
import ComingSoonPage from "../views/common/ComingSoonPage";
import CompleteProfile from "../views/profileOnboarding/CompleteProfile";
import UserProfilePage from "../views/profile/UserProfilePage";
import NotFound from "../views/common/NotFound";

const AppLayout = () => {
  const location = useLocation();
  useScrollToTop();

  const fullBleedRoutes = ["/about", "/features/ai-highlights", "/features/scoring", "/streamer-dashboard", "/my-team", "/profile"];

  const mainWrapRoutes = ["/tournaments/team-info", "/tournaments/create-tournament", "/tournaments/schedule-match", "/create-league"];

  const isMyLeaguesIndex = location.pathname === "/my-leagues";
  const isFullBleedRoute = isMyLeaguesIndex || fullBleedRoutes.some((p) => location.pathname.startsWith(p));

  const useMainWrap = mainWrapRoutes.some((p) => location.pathname.startsWith(p));
  return (
    <>
      <ParentHeader />
      <div className={isFullBleedRoute ? "page-full-bleed" : useMainWrap ? "main-wrap" : "main-wrapper"}>
        <Outlet />
      </div>
      <Footer />
    </>
  );
};

const PremiumStatGptRoute = () => {
  const token = getAuthToken();
  if (!hasPremiumAccess(token)) {
    return <Navigate to="/pricing" replace />;
  }
  return <StatGpt />;
};

const CreateLeagueRoute = () => {
  const token = getAuthToken();
  const dispatch = useAppDispatch();

  if (!token) {
    toast.error("Please login or signup to create a league.");
    dispatch(openAuth("signin"));
    return <Navigate to="/home" replace />;
  }
  return <CreateLeague />;
};

const HomeRoute = () => {
  const [, setAuthRefresh] = useState(0);
  const token = getAuthToken();

  // Re-render when token is set (e.g. user logs in via modal while already on /)
  useEffect(() => {
    const handler = () => setAuthRefresh((n) => n + 1);
    window.addEventListener("auth-token-updated", handler);
    return () => window.removeEventListener("auth-token-updated", handler);
  }, []);

  return token ? <LoggedInHomePage /> : <HomePage />;
};

const Routing = () => {
  return (
    <>
      <BrowserRouter>
        {/* <BrowserRouter> */}
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route element={<AppLayout />}>
            <Route path="/profile" element={<UserProfilePage />} />
            <Route element={<PublicRoute />}>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/home" element={<LoggedInHomePage />} />
              <Route path="/leagues-home" element={<TabContentWrapper />} />
              <Route path="/tournamentDetails/:tournamentId" element={<TournamentsDetails />} />
              <Route path="/tournaments/tournaments-details" element={<TournamentsDetails />} />
              <Route path="/tournaments/tournaments-details/gallery-details" element={<TournamentsDetails />} />
              <Route path="/tournaments/tournaments-matches" element={<Matches />} />
              <Route path="/tournaments/create-tournament" element={<CreateTournament />} />
              <Route path="/tournaments/schedule-match" element={<ScheduleMatchPage />} />
              <Route path="/tournaments/create-division" element={<CreateDivisionPage />} />
              <Route path="/tournaments/add-players" element={<AddPlayersPage />} />
              <Route path="/tournaments/team-info" element={<TeamInfo />} />
              <Route path="/tournaments/team-info/gallery-details" element={<TeamInfo />} />
              <Route path="/tournaments/player-profile" element={<PlayerProfile />} />
              <Route path="/matches-details/:matchId" element={<MatchesDetails />} />
              <Route path="/ground-details/:groundId" element={<GroundDetails />} />
              <Route path="/stat-gpt" element={<PremiumStatGptRoute />} />
              <Route path="/pricing" element={<PricingPlans />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/coming-soon" element={<ComingSoonPage />} />
              <Route path="/features/ai-highlights" element={<AiHighlightsPage />} />
              <Route path="/my-leagues" element={<MyLeagues />} />
              <Route path="/my-leagues/:leagueId" element={<LeagueRouteHydrator />}>
                <Route index element={<TabContentWrapper />} />
                <Route path="tournaments/:tournamentId" element={<TournamentRouteHydrator />} />
              </Route>
              <Route path="/create-league" element={<CreateLeagueRoute />} />
              <Route path="/features/scoring" element={<ScoringPage />} />
              <Route path="/streamer-dashboard" element={<StreamerDashboard />} />
              <Route path="/streamer-dashboard/asset-setup" element={<AssetSetup />} />
              <Route path="/features/league-manager" element={<LeagueManagerComingSoon />} />
              <Route path="/features/live-streaming" element={<LiveStreamingComingSoon />} />
              <Route path="/my-team" element={<Outlet />}>
                <Route index element={<MyTeam />} />
                <Route path=":teamId" element={<MyTeamDetail />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        {/* </BrowserRouter> */}
      </BrowserRouter>
    </>
  );
};

export default Routing;
